import React, { useEffect, useState } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Autocomplete,
  InputAdornment,
  Typography,
  Alert,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { enGB } from 'date-fns/locale';
import {
  POLICY_TYPES,
  PREMIUM_FREQUENCIES,
  POLICY_STATUSES,
  INSURERS,
  PolicyTypeIcon,
  policyType,
  parseDate,
  toDateString,
  formatCover,
} from '../../utils/insurance';

const emptyForm = {
  policyType: 'term',
  policyName: '',
  insurer: '',
  policyNumber: '',
  insured: '',
  nominee: '',
  sumAssured: '',
  premiumAmount: '',
  premiumFrequency: 'yearly',
  startDate: null,
  endDate: null,
  nextDueDate: null,
  agent: '',
  status: 'active',
  notes: '',
};

const formFromPolicy = (policy) => ({
  policyType: policy.policy_type || 'other',
  policyName: policy.policy_name || '',
  insurer: policy.insurer || '',
  policyNumber: policy.policy_number || '',
  insured: policy.insured || '',
  nominee: policy.nominee || '',
  sumAssured: policy.sum_assured != null ? String(policy.sum_assured) : '',
  premiumAmount: policy.premium_amount != null ? String(policy.premium_amount) : '',
  premiumFrequency: policy.premium_frequency || 'yearly',
  startDate: parseDate(policy.start_date),
  endDate: parseDate(policy.end_date),
  nextDueDate: parseDate(policy.next_due_date),
  agent: policy.agent || '',
  status: policy.status || 'active',
  notes: policy.notes || '',
});

const validDate = (date) => !date || !isNaN(date.getTime());

// Add / edit an insurance policy. Saves to `insurance_policies` and calls onSaved(message).
const PolicyDialog = ({ open, policy, onClose, onSaved, supabase, user }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(policy ? formFromPolicy(policy) : emptyForm);
    setError('');
  }, [open, policy]);

  const set = (field) => (e) => { setForm(prev => ({ ...prev, [field]: e.target.value })); setError(''); };
  const setDate = (field) => (value) => { setForm(prev => ({ ...prev, [field]: value })); setError(''); };

  const handleSave = async () => {
    if (!form.policyName.trim()) {
      setError('Please enter the policy / plan name');
      return;
    }
    const sumAssured = form.sumAssured === '' ? 0 : parseFloat(form.sumAssured);
    const premiumAmount = form.premiumAmount === '' ? 0 : parseFloat(form.premiumAmount);
    if (!isFinite(sumAssured) || sumAssured < 0 || !isFinite(premiumAmount) || premiumAmount < 0) {
      setError('Please enter valid amounts (zero or more)');
      return;
    }
    if (![form.startDate, form.endDate, form.nextDueDate].every(validDate)) {
      setError('Please check the dates');
      return;
    }
    if (form.startDate && form.endDate && form.endDate < form.startDate) {
      setError('The end date must be after the start date');
      return;
    }

    setSaving(true);
    const payload = {
      policy_type: form.policyType,
      policy_name: form.policyName.trim(),
      insurer: form.insurer.trim(),
      policy_number: form.policyNumber.trim(),
      insured: form.insured.trim(),
      nominee: form.nominee.trim(),
      sum_assured: sumAssured,
      premium_amount: premiumAmount,
      premium_frequency: form.premiumFrequency,
      start_date: toDateString(form.startDate),
      end_date: toDateString(form.endDate),
      // A single (one-time) premium has nothing more to pay
      next_due_date: form.premiumFrequency === 'single' ? null : toDateString(form.nextDueDate),
      agent: form.agent.trim(),
      status: form.status,
      notes: form.notes.trim(),
    };
    const { error: saveError } = policy
      ? await supabase.from('insurance_policies').update(payload).eq('id', policy.id).eq('user_id', user.id)
      : await supabase.from('insurance_policies').insert({ ...payload, user_id: user.id });
    setSaving(false);

    if (saveError) {
      console.error('Error saving insurance policy:', saveError);
      setError(`Failed to ${policy ? 'update' : 'save'} the policy`);
      return;
    }
    onSaved(policy ? 'Policy updated successfully' : 'Policy added successfully');
  };

  const type = policyType(form.policyType);
  const isSingle = form.premiumFrequency === 'single';
  const coverPreview = parseFloat(form.sumAssured) > 0 ? formatCover(parseFloat(form.sumAssured)) : null;
  const section = (title) => (
    <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.5, mt: 0.5, mb: -0.75 }}>{title}</Typography>
  );

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth fullScreen={fullScreen}>
      <DialogTitle>{policy ? 'Edit Policy' : 'Add Policy'}</DialogTitle>
      <DialogContent dividers>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {section('Policy')}
            <TextField select label="Policy Type" required value={form.policyType} onChange={set('policyType')}>
              {POLICY_TYPES.map(t => (
                <MenuItem key={t.value} value={t.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    <PolicyTypeIcon type={t.value} fontSize="small" sx={{ color: t.color }} />
                    {t.label}
                  </Box>
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Policy / Plan Name"
              required
              autoFocus={!policy}
              placeholder="e.g., Click 2 Protect Super"
              value={form.policyName}
              onChange={set('policyName')}
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <Autocomplete
                freeSolo
                options={INSURERS}
                inputValue={form.insurer}
                onInputChange={(e, value) => setForm(prev => ({ ...prev, insurer: value }))}
                renderInput={(params) => <TextField {...params} label="Insurer" placeholder="e.g., HDFC Life" />}
              />
              <TextField label="Policy Number" value={form.policyNumber} onChange={set('policyNumber')} />
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField label="Insured / Covered" placeholder={type.insuredHint} value={form.insured} onChange={set('insured')} />
              <TextField label="Nominee" placeholder="e.g., Spouse" value={form.nominee} onChange={set('nominee')} />
            </Box>

            {section('Cover & premium')}
            <TextField
              label={form.policyType === 'motor' ? 'Cover (IDV)' : 'Sum Assured / Cover'}
              type="number"
              inputProps={{ min: 0, step: 'any', inputMode: 'decimal' }}
              InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
              value={form.sumAssured}
              onChange={set('sumAssured')}
              helperText={coverPreview || ' '}
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField
                label="Premium Amount"
                type="number"
                inputProps={{ min: 0, step: 'any', inputMode: 'decimal' }}
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                value={form.premiumAmount}
                onChange={set('premiumAmount')}
              />
              <TextField select label="Paid" value={form.premiumFrequency} onChange={set('premiumFrequency')}>
                {PREMIUM_FREQUENCIES.map(f => <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>)}
              </TextField>
            </Box>

            {section('Dates')}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <DatePicker
                label="Start Date"
                value={form.startDate}
                onChange={setDate('startDate')}
                format="dd MMM yyyy"
                slotProps={{ textField: { fullWidth: true }, field: { clearable: true } }}
              />
              <DatePicker
                label={['term', 'life'].includes(form.policyType) ? 'Maturity / End Date' : 'Expiry / End Date'}
                value={form.endDate}
                onChange={setDate('endDate')}
                format="dd MMM yyyy"
                slotProps={{ textField: { fullWidth: true }, field: { clearable: true } }}
              />
            </Box>
            {!isSingle && (
              <DatePicker
                label="Next Premium / Renewal Due"
                value={form.nextDueDate}
                onChange={setDate('nextDueDate')}
                format="dd MMM yyyy"
                slotProps={{
                  textField: { fullWidth: true, helperText: 'Moves forward automatically when you record a premium' },
                  field: { clearable: true },
                }}
              />
            )}

            {section('Other')}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField label="Agent / Advisor" placeholder="Name and phone" value={form.agent} onChange={set('agent')} />
              <TextField select label="Status" value={form.status} onChange={set('status')}>
                {POLICY_STATUSES.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
              </TextField>
            </Box>
            <TextField
              label="Notes"
              multiline
              minRows={2}
              placeholder="e.g., Riders, room rent limit, no-claim bonus, where the papers are kept"
              value={form.notes}
              onChange={set('notes')}
            />
            {error && <Alert severity="error">{error}</Alert>}
          </Box>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : policy ? 'Update' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PolicyDialog;
